import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useCan } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  NotePencil, Plus, Trash, PencilSimple, Calendar, User as UserIcon,
  CheckCircle, Warning, ArrowRight, Info, BellRinging,
} from '@phosphor-icons/react';

const PRIORITIES = [
  { value: 'high',   label: 'High',   className: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'normal', label: 'Normal', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'low',    label: 'Low',    className: 'bg-zinc-50 text-zinc-600 border-zinc-200' },
];

function priorityStyle(p) {
  return PRIORITIES.find(x => x.value === p) || PRIORITIES[1];
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    // Accept "YYYY-MM-DD" date-only or full ISO; render in IN locale.
    const d = iso.length <= 10 ? new Date(iso + 'T00:00:00') : new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
}

function isOverdueDate(dateStr) {
  // dateStr is "YYYY-MM-DD" — overdue if strictly before today (local).
  if (!dateStr) return false;
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr + 'T00:00:00');
    return d < today;
  } catch { return false; }
}

/* Reminder preset options. Each yields an offset relative to "now" in minutes,
   except "custom" which opens the inline datetime picker, and "none" which
   clears the reminder. The order they appear in the form is the user-visible
   order. */
const REMINDER_PRESETS = [
  { value: 'none',    label: 'No reminder', minutes: null },
  { value: '15m',     label: 'In 15 mins',  minutes: 15 },
  { value: '1h',      label: 'In 1 hour',   minutes: 60 },
  { value: '3h',      label: 'In 3 hours',  minutes: 180 },
  { value: 'tomorrow',label: 'Tomorrow 9am',minutes: 'tomorrow-9' },
  { value: 'week',    label: 'In 1 week',   minutes: 60 * 24 * 7 },
  { value: 'custom',  label: 'Custom…',     minutes: 'custom' },
];

function presetToISO(preset) {
  if (preset.value === 'none' || preset.value === 'custom') return null;
  if (preset.minutes === 'tomorrow-9') {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(9, 0, 0, 0);
    return t.toISOString();
  }
  return new Date(Date.now() + preset.minutes * 60 * 1000).toISOString();
}

export default function NotesPage() {
  const canEdit = useCan('notes', 'edit');
  const canDelete = useCan('notes', 'delete');

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');     // all | pending | completed
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notes');
      setNotes(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'pending') return notes.filter(n => !n.completed);
    if (filter === 'completed') return notes.filter(n => n.completed);
    return notes;
  }, [notes, filter]);

  const stats = useMemo(() => {
    const pending = notes.filter(n => !n.completed);
    const overdue = pending.filter(n => isOverdueDate(n.due_at));
    const high = pending.filter(n => n.priority === 'high');
    return { total: notes.length, pending: pending.length, overdue: overdue.length, high: high.length };
  }, [notes]);

  const [staffStats, setStaffStats] = useState([]);
  useEffect(() => {
    api.get('/notes/staff-stats').then(r => setStaffStats(r.data)).catch(() => setStaffStats([]));
  }, [notes]);

  const toggleComplete = async (note) => {
    if (!canEdit) return;
    try {
      await api.put(`/notes/${note.id}`, { completed: !note.completed });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-end gap-3">
        {canEdit && (
          <Button
            onClick={() => { setEditTarget(null); setShowForm(true); }}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
            data-testid="add-note-btn"
          >
            <Plus size={16} className="mr-1.5" /> Add Note
          </Button>
        )}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="notes-stats">
        <Stat label="Total"    value={stats.total}    color="text-[#0A0A0A]" />
        <Stat label="Pending"  value={stats.pending}  color="text-[#2563EB]" />
        <Stat label="Overdue"  value={stats.overdue}  color="text-red-600" />
        <Stat label="High Priority" value={stats.high} color="text-amber-600" />
      </div>

      {/* Staff-wise breakdown */}
      {staffStats.length > 0 && (
        <Card className="border-[#E4E4E7]" data-testid="notes-staff-stats">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-2">
                <UserIcon size={15} weight="duotone" className="text-[#7C3AED]" />
                Staff-wise Notes
              </h3>
              <span className="text-[11px] text-[#A1A1AA]">total · due · closed</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {staffStats.map((s) => (
                <div
                  key={s.user_id}
                  className="flex items-center justify-between px-3 py-2 rounded-md border border-[#E4E4E7] bg-white"
                  data-testid={`notes-staff-row-${s.user_id}`}
                >
                  <div className="text-sm font-medium text-[#0A0A0A] truncate">{s.name}</div>
                  <div className="flex items-center gap-1.5 text-[11px] shrink-0">
                    <Pill label={s.total} tone="zinc" testid={`staff-${s.user_id}-total`} />
                    <Pill label={s.due}   tone="red"  testid={`staff-${s.user_id}-due`} />
                    <Pill label={s.closed} tone="emerald" testid={`staff-${s.user_id}-closed`} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter pills */}
      <div className="flex items-center gap-2 text-sm" data-testid="notes-filter">
        {[
          { v: 'all', label: 'All' },
          { v: 'pending', label: 'Pending' },
          { v: 'completed', label: 'Completed' },
        ].map(p => (
          <button
            key={p.v}
            onClick={() => setFilter(p.v)}
            data-testid={`notes-filter-${p.v}`}
            className={`px-3 py-1.5 rounded-full border transition-colors ${
              filter === p.v
                ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Notes list */}
      {loading ? (
        <div className="text-sm text-[#52525B]">Loading notes…</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-[#E4E4E7]">
          <CardContent className="py-14 text-center">
            <NotePencil size={36} weight="duotone" className="mx-auto text-[#A1A1AA] mb-3" />
            <p className="text-sm text-[#52525B]">No {filter !== 'all' ? filter : ''} notes yet.</p>
            {canEdit && filter !== 'completed' && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => { setEditTarget(null); setShowForm(true); }}
              >
                <Plus size={14} className="mr-1" /> Create your first note
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E4E4E7]">
          <CardContent className="p-0">
            <ul className="divide-y divide-[#F4F4F5]" data-testid="notes-list">
              {filtered.map((n) => (
                <NoteRow
                  key={n.id}
                  note={n}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onToggle={() => toggleComplete(n)}
                  onEdit={() => { setEditTarget(n); setShowForm(true); }}
                  onDelete={() => setDeleteTarget(n)}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit dialog */}
      <NoteFormDialog
        open={showForm}
        target={editTarget}
        onOpenChange={(o) => { setShowForm(o); if (!o) setEditTarget(null); }}
        onSaved={load}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-note-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" will be removed permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                const t = deleteTarget;
                try {
                  await api.delete(`/notes/${t.id}`);
                  toast.success('Note deleted');
                  setDeleteTarget(null);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || err.message);
                }
              }}
              data-testid="delete-note-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <Card className="border-[#E4E4E7]">
      <CardContent className="px-4 py-3">
        <div className="text-[11px] uppercase tracking-wider text-[#52525B]">{label}</div>
        <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

const PILL_TONE = {
  zinc:    'bg-[#F4F4F5] text-[#0A0A0A]',
  red:     'bg-red-100 text-red-700',
  emerald: 'bg-emerald-100 text-emerald-700',
};
function Pill({ label, tone = 'zinc', testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-md font-semibold ${PILL_TONE[tone]}`}
    >
      {label}
    </span>
  );
}

function NoteRow({ note, canEdit, canDelete, onToggle, onEdit, onDelete }) {
  const ps = priorityStyle(note.priority);
  const overdue = !note.completed && isOverdueDate(note.due_at);
  const reminderActive = !note.completed && note.reminder_at && new Date(note.reminder_at) <= new Date();

  return (
    <li
      className={`group flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFAFA] transition-colors ${note.completed ? 'opacity-70' : ''} ${reminderActive ? 'bg-[#FEF3C7]/40' : ''}`}
      data-testid={`note-row-${note.id}`}
    >
      <Checkbox
        checked={note.completed}
        disabled={!canEdit}
        onCheckedChange={onToggle}
        className="shrink-0"
        data-testid={`note-toggle-${note.id}`}
      />

      {/* Bold notes content + priority + due date — main column */}
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        <span className={`text-sm font-bold leading-snug whitespace-pre-line ${note.completed ? 'line-through text-[#52525B]' : 'text-[#0A0A0A]'}`}>
          {note.title}
          {note.body && (note.completed ? ' ' : '\n') + note.body}
        </span>
        <Badge className={`${ps.className} font-medium border uppercase text-[9px] tracking-wide shrink-0`}>
          {ps.label}
        </Badge>
        {note.due_at && (
          <span className={`text-[11px] inline-flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : 'text-[#52525B]'}`}>
            {overdue ? <Warning size={11} weight="fill" /> : <Calendar size={11} />}
            Due {formatDate(note.due_at)}
          </span>
        )}
        {reminderActive && (
          <span className="text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#B45309] font-semibold">
            <BellRinging size={11} weight="fill" /> Reminder
          </span>
        )}
      </div>

      {/* Right side: creator name + info tooltip + actions */}
      <div className="flex items-center gap-1.5 shrink-0 text-[11px] text-[#52525B]">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#F4F4F5] text-[#0A0A0A] font-medium">
          <UserIcon size={11} /> {note.created_by_name || '—'}
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-[#F4F4F5] text-[#52525B]"
              data-testid={`note-info-${note.id}`}
              aria-label="Details"
            >
              <Info size={13} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            <div className="space-y-1 text-xs">
              <div>
                <span className="text-[#A1A1AA]">Created by</span>{' '}
                <span className="font-medium">{note.created_by_name || '—'}</span>
              </div>
              <div>
                <span className="text-[#A1A1AA]">on</span>{' '}
                <span className="font-medium">{formatDateTime(note.created_at)}</span>
              </div>
              {note.due_at && (
                <div className="pt-1 border-t border-white/10">
                  <span className="text-[#A1A1AA]">Due</span>{' '}
                  <span className="font-medium">{formatDate(note.due_at)}</span>
                </div>
              )}
              {note.reminder_at && (
                <div className="pt-1 border-t border-white/10">
                  <span className="text-[#A1A1AA]">Reminder at</span>{' '}
                  <span className="font-medium">{formatDateTime(note.reminder_at)}</span>
                </div>
              )}
              {note.completed && (
                <div className="pt-1 border-t border-white/10">
                  <div>
                    <span className="text-[#A1A1AA]">Completed by</span>{' '}
                    <span className="font-medium">{note.completed_by_name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#A1A1AA]">on</span>{' '}
                    <span className="font-medium">{formatDateTime(note.completed_at)}</span>
                  </div>
                </div>
              )}
              {note.updated_at && !note.completed && (
                <div className="pt-1 border-t border-white/10">
                  <span className="text-[#A1A1AA]">Last updated</span>{' '}
                  <span className="font-medium">{formatDateTime(note.updated_at)}</span>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>

        {canEdit && (
          <button
            onClick={onEdit}
            title="Edit"
            className="p-1.5 rounded-md hover:bg-[#F4F4F5] text-[#52525B]"
            data-testid={`note-edit-${note.id}`}
          >
            <PencilSimple size={13} />
          </button>
        )}
        {canDelete && (
          <button
            onClick={onDelete}
            title="Delete"
            className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
            data-testid={`note-delete-${note.id}`}
          >
            <Trash size={13} />
          </button>
        )}
      </div>
    </li>
  );
}

function NoteFormDialog({ open, target, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ notes: '', priority: 'normal', due_at: '', reminder_at: '', preset: 'none' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      if (target) {
        // Combine the legacy title + body into a single "notes" string.
        const merged = [target.title, target.body].filter(Boolean).join('\n');
        setForm({
          notes: merged,
          priority: target.priority || 'normal',
          due_at: target.due_at || '',
          reminder_at: target.reminder_at ? toLocalInputValue(target.reminder_at) : '',
          preset: target.reminder_at ? 'custom' : 'none',
        });
      } else {
        setForm({ notes: '', priority: 'normal', due_at: '', reminder_at: '', preset: 'none' });
      }
      setErr('');
    }
  }, [open, target]);

  const applyPreset = (p) => {
    if (p.value === 'custom') {
      setForm(f => ({ ...f, preset: 'custom' }));
      return;
    }
    if (p.value === 'none') {
      setForm(f => ({ ...f, preset: 'none', reminder_at: '' }));
      return;
    }
    const iso = presetToISO(p);
    setForm(f => ({ ...f, preset: p.value, reminder_at: iso ? toLocalInputValue(iso) : '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = form.notes.trim();
    if (!trimmed) { setErr('Please write something'); return; }
    setSaving(true); setErr('');
    try {
      // Title carries the whole note. We always blank out `body` so the merged
      // model stays the source of truth.
      const payload = {
        title: trimmed,
        body: '',
        priority: form.priority,
        due_at: form.due_at || null,
        reminder_at: form.reminder_at ? new Date(form.reminder_at).toISOString() : null,
      };
      if (target) {
        await api.put(`/notes/${target.id}`, payload);
        toast.success('Note updated');
      } else {
        await api.post('/notes', payload);
        toast.success('Note created');
      }
      onOpenChange(false);
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="note-form-dialog">
        <DialogHeader>
          <DialogTitle>{target ? 'Edit note' : 'Add a new note'}</DialogTitle>
          <DialogDescription>Notes are private to your company. Add a due date or a reminder for time-sensitive items.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#52525B] mb-1 block">Notes *</Label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Write your note here…"
              rows={5}
              required
              autoFocus
              className="w-full rounded-md border border-[#E4E4E7] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
              data-testid="note-notes-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#52525B] mb-1 block">Priority</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setForm({ ...form, priority: p.value })}
                  data-testid={`note-priority-${p.value}`}
                  className={`text-xs py-1.5 rounded-md border transition-colors ${
                    form.priority === p.value
                      ? `${p.className} font-semibold`
                      : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#52525B] mb-1 block">Due date</Label>
            <Input
              type="date"
              value={form.due_at}
              onChange={(e) => setForm({ ...form, due_at: e.target.value })}
              data-testid="note-due-input"
            />
            <p className="text-[10px] text-[#A1A1AA] mt-1">Date only — no time.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#52525B] mb-1 flex items-center gap-1">
              <BellRinging size={12} weight="duotone" /> Reminder
            </Label>
            <div className="flex flex-wrap gap-1.5" data-testid="note-reminder-presets">
              {REMINDER_PRESETS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => applyPreset(p)}
                  data-testid={`note-reminder-preset-${p.value}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    form.preset === p.value
                      ? 'bg-[#7C3AED] text-white border-[#7C3AED]'
                      : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {form.preset === 'custom' && (
              <Input
                type="datetime-local"
                value={form.reminder_at}
                onChange={(e) => setForm({ ...form, reminder_at: e.target.value })}
                data-testid="note-reminder-input"
                className="mt-2"
              />
            )}
            {form.preset !== 'none' && form.preset !== 'custom' && form.reminder_at && (
              <p className="text-[10px] text-[#7C3AED] mt-1.5 font-medium">
                Will fire on {formatDateTime(new Date(form.reminder_at).toISOString())}
              </p>
            )}
            <p className="text-[10px] text-[#A1A1AA] mt-1">Visible to creator + admin only.</p>
          </div>
          {err && <div className="text-sm text-red-500" data-testid="note-form-error">{err}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              data-testid="note-form-submit"
            >
              {saving ? 'Saving…' : (target ? 'Save changes' : 'Create note')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Convert ISO UTC → "YYYY-MM-DDTHH:MM" usable inside <input type="datetime-local">. */
function toLocalInputValue(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

/* -------------------------------------------------------------------------- */
/* Compact dashboard widget — exported for DashboardPage to mount.            */
/* -------------------------------------------------------------------------- */
export function NotesReminderWidget() {
  const { user, permissions } = useAuth();
  const allowed = user?.role === 'admin' || Boolean(permissions?.notes?.view);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/notes/reminders?limit=5');
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [allowed]);

  if (!allowed) return null;

  return (
    <Card className="border-[#E4E4E7]" data-testid="dashboard-reminders">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <NotePencil size={18} weight="duotone" className="text-[#7C3AED]" />
            <h3 className="text-sm font-semibold text-[#0A0A0A]">Reminders</h3>
          </div>
          <Link
            to="/notes"
            className="text-xs text-[#7C3AED] hover:underline inline-flex items-center gap-0.5"
            data-testid="dashboard-reminders-viewall"
          >
            View all <ArrowRight size={12} />
          </Link>
        </div>
        {loading ? (
          <p className="text-xs text-[#52525B]">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle size={24} weight="duotone" className="mx-auto text-emerald-500 mb-1.5" />
            <p className="text-xs text-[#52525B]">All caught up — no pending notes.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map(n => {
              const ps = priorityStyle(n.priority);
              return (
                <li
                  key={n.id}
                  className="flex items-start gap-2.5 px-2 py-2 rounded-md hover:bg-[#FAFAFA] transition-colors"
                >
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                    n.priority === 'high' ? 'bg-red-500'
                    : n.priority === 'normal' ? 'bg-blue-500'
                    : 'bg-zinc-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#0A0A0A] truncate">{n.title}</div>
                    <div className="flex items-center gap-2 text-[11px] text-[#52525B] mt-0.5 flex-wrap">
                      <span className="uppercase tracking-wide font-semibold" style={{ color: ps.className.match(/text-\S+/)?.[0]?.replace(/^text-\[?|\]?$/g, '') }}>
                        {ps.label}
                      </span>
                      {n.reminder_at && (
                        <span className="inline-flex items-center gap-0.5 text-[#B45309] font-semibold">
                          <BellRinging size={11} weight="fill" /> {formatDateTime(n.reminder_at)}
                        </span>
                      )}
                      {n.due_at && !n.reminder_at && (
                        <span className={isOverdueDate(n.due_at) ? 'text-red-600 font-semibold' : ''}>
                          {isOverdueDate(n.due_at) && <Warning size={11} weight="fill" className="inline mr-0.5 -mt-0.5" />}
                          Due {formatDate(n.due_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
